// widget/src/ChatWidget.tsx
//
// Visual port of docs/widget-concept-v2.html. Functionally wraps the
// existing useChatStream / useRecorder / usePageContext hooks — none of
// the data-flow code was touched in this rebuild.

import { useCallback, useEffect, useRef, useState } from "react"
import type { PageContext, WidgetMountOptions } from "./types"
import { MessageCard } from "./components/MessageCard"
import { Composer } from "./components/Composer"
import { VoiceOrb } from "./components/VoiceOrb"
import { Chips } from "./components/Chips"
import { useChatStream } from "./hooks/useChatStream"
import { useRecorder } from "./hooks/useRecorder"
import { usePageContext } from "./hooks/usePageContext"
import { useSyncPageContext } from "./hooks/useSyncPageContext"
import { useProactive } from "./hooks/useProactive"
import { useMobileSheet } from "./hooks/useMobileSheet"
import { cn } from "./util"
import "./styles/tokens.css"
import styles from "./styles/widget.module.css"

// Shown when Pearl proactively opens but no approved per-product opener
// exists yet — a safe, generic offer of help.
const PROACTIVE_FALLBACK_GENERIC =
  "Looking at this one? I can help with sizing, colors, or how to apply it."

// Per-product fallback when there's no curated opener. Mirrors the FinChat
// version so both UI variants stay in sync — keep them aligned if either
// changes.
function buildProactiveFallback(productTitle: string | undefined): string {
  if (!productTitle) return PROACTIVE_FALLBACK_GENERIC
  const t = truncateAtWord(productTitle.replace(/^(the|a|an)\s+/i, ""), 50)
  if (!t) return PROACTIVE_FALLBACK_GENERIC
  return `Looking at the ${t}? I can help with sizing, color match, or application.`
}

/** Word-boundary truncate — see FinChatWidget for full docs. Duplicated
 *  rather than shared because the two widget variants are otherwise
 *  independent files. */
function truncateAtWord(s: string, max: number): string {
  if (!s) return ""
  if (s.length <= max) return s
  const slice = s.slice(0, max)
  const lastSpace = slice.lastIndexOf(" ")
  if (lastSpace < 8) return ""
  return slice.slice(0, lastSpace).trim()
}

// Welcome state. Intentionally plain: no "Hey 👋", no "AI helper" framing,
// no emoji-prefixed chips. The pills are direct asks in the voice of a
// customer, not categories in the voice of a bot — pick one and Pearl
// reads it as if the customer typed it.
const WELCOME_TEXT =
  "Hi, I'm Pearl, DYC's AI assistant (in beta). Ask me about paint, sizing, OEM colors, or your order — I'll bring in a human if I'm stuck."
const WELCOME_CHIPS = [
  "Help finding a color",
  "Peelable vs Permanent Paint",
  "How much paint do I need?",
  "Track my order",
  "I need to talk to a human",
]

interface Props {
  mountOptions: WidgetMountOptions
  sessionToken: string
  initialContext: PageContext
  /** Idle-pill copy (next to the orb). Kept for back-compat / dashboard
   *  customizer plumbing but no longer rendered — the persistent "Ask
   *  Pearl anything" sticker read as a chatbot dock label. The orb alone
   *  is the CTA now. */
  dockLabel?: string
  /** A/B experiment variants. Unused in classic widget for now — Fin
   *  variant consumes them — but accepted so the shared mount can pass
   *  the prop unconditionally. */
  experiments?: Record<string, string>
  /** Post-resolution CSAT prompt flag. Unused in the classic widget (only
   *  the Fin variant renders the rating prompt) but accepted so the shared
   *  mount can pass it unconditionally. */
  csatEnabled?: boolean
}

export function ChatWidget(props: Props) {
  const [active, setActive] = useState(false)
  const [dismissedHint, setDismissedHint] = useState(false)
  // When Pearl proactively opens, this holds the opener text shown in the
  // first bubble (per-product opener, or PROACTIVE_FALLBACK). null = the
  // chat was opened by the customer, so the normal welcome shows.
  const [proactiveText, setProactiveText] = useState<string | null>(null)
  // Curated quick-reply chips from pearl_product_openers.chips when
  // populated (jsonb array → string[]). null = use the default WELCOME_CHIPS.
  const [curatedChips, setCuratedChips] = useState<string[] | null>(null)

  const pageContext = usePageContext({
    customerEmail: props.initialContext.customerEmail,
    customerId: props.initialContext.customerId,
  })
  // Push every page navigation to the server in real-time (matches the
  // FinChatWidget wiring — see useSyncPageContext for why).
  useSyncPageContext(props.mountOptions.apiBaseUrl, props.sessionToken, pageContext)
  const { messages, pending, send, hadPersistedHistory, clearPersisted } =
    useChatStream({
      apiBaseUrl: props.mountOptions.apiBaseUrl,
      sessionToken: props.sessionToken,
      pageContext,
    })

  const recorder = useRecorder()
  const messagesRef = useRef<HTMLDivElement>(null)

  // Proactive engagement — product-page dwell trigger + guardrails.
  const handleProactiveFire = useCallback(
    (opener: string | null, chips: string[] | null) => {
      setProactiveText(
        opener ?? buildProactiveFallback(pageContext.productTitle),
      )
      setCuratedChips(chips)
      setActive(true)
      setDismissedHint(true)
    },
    [pageContext.productTitle],
  )
  const { dismissProactive } = useProactive({
    apiBaseUrl: props.mountOptions.apiBaseUrl,
    pageContext,
    active,
    hasEngaged: messages.length > 0,
    onFire: handleProactiveFire,
  })

  // Mobile sheet behaviors: lock the storefront scroll behind the chat and
  // track the soft keyboard so the composer stays pinned above it.
  useMobileSheet(active)

  // Auto-scroll messages to bottom on new content.
  useEffect(() => {
    const el = messagesRef.current
    if (!el) return
    requestAnimationFrame(() => {
      el.scrollTop = el.scrollHeight
    })
  }, [messages, pending])

  // Esc closes when active.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape" && active) setActive(false)
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [active])

  const open = useCallback(() => {
    setActive(true)
    setDismissedHint(true)
  }, [])
  const close = useCallback(() => {
    setActive(false)
    // If the customer closes a proactive open without engaging, treat it
    // as a "no thanks" and suppress further proactive opens this session.
    if (proactiveText !== null && messages.length === 0) {
      dismissProactive()
    }
    setProactiveText(null)
    // Explicit close ends open-state continuity: drop the "was open" flag
    // and the persisted thread so the next navigation stays closed.
    try {
      sessionStorage.removeItem("pearl-was-open")
    } catch {
      /* ignore */
    }
    clearPersisted()
  }, [proactiveText, messages.length, dismissProactive, clearPersisted])

  // Open-state continuity: if a conversation was open when the customer
  // navigated (same-tab), reopen the panel on the new page so the thread
  // continues instead of resetting to the idle orb. Restored messages
  // come from useChatStream's sessionStorage hydration.
  useEffect(() => {
    try {
      if (hadPersistedHistory && sessionStorage.getItem("pearl-was-open") === "1") {
        setActive(true)
      }
    } catch {
      /* ignore */
    }
    // Mount-only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Mark "was open" while the panel is open with an active conversation,
  // so a navigation mid-thread reopens it. Cleared on explicit close.
  useEffect(() => {
    if (active && messages.length > 0) {
      try {
        sessionStorage.setItem("pearl-was-open", "1")
      } catch {
        /* ignore */
      }
    }
  }, [active, messages.length])

  async function transcribe(blob: Blob): Promise<string> {
    const fd = new FormData()
    fd.append("audio", blob, "voice.webm")
    const res = await fetch(`${props.mountOptions.apiBaseUrl}/api/transcribe`, {
      method: "POST",
      body: fd,
    })
    if (!res.ok) throw new Error("Transcribe failed: " + res.status)
    const data = await res.json()
    return (data.text as string | undefined) ?? ""
  }

  // Voice IN flow: when a voice blob is captured, transcribe + auto-send.
  const handleVoiceBlob = useCallback(
    async (blob: Blob | null) => {
      if (!active) setActive(true)
      setDismissedHint(true)
      if (!blob) return
      try {
        const text = await transcribe(blob)
        if (text.trim()) send(text.trim())
      } catch (err) {
        console.error("[pearl] transcribe failed:", err)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [active, send],
  )

  const handleSend = useCallback(
    (text: string) => {
      if (!text.trim()) return
      setDismissedHint(true)
      if (!active) setActive(true)
      send(text)
    },
    [active, send],
  )

  // Tap-on-orb-without-hold opens the chat (the VoiceOrb component owns
  // the press-vs-hold detection and signals via `onTap`).
  const handleOrbTap = useCallback(() => {
    if (!active) open()
  }, [active, open])

  const isThinking = pending && !recorder.isRecording

  return (
    <div
      className={cn(
        styles["widget-host"],
        active && styles.active,
        isThinking && styles.thinking,
        recorder.isRecording && styles.recording,
        dismissedHint && !active && styles["dismissed-hint"],
      )}
    >
      <div className={styles.scrim} onClick={close} />

      <div className={styles["orb-stack"]}>
        {/* The idle hint pill was removed in favor of the bare orb. On
          * mobile, the orb-stack is the dock pill — see the mobile
          * sheet styles for the centered orb treatment. */}

        {/* Mobile-only sheet header — hidden on desktop via CSS. The
          * desktop close X is the floating .close-pill below. */}
        <div className={styles["sheet-header"]}>
          <div className={styles["sheet-title"]}>
            <span className={styles["sheet-avatar"]} aria-hidden="true">P</span>
            <div className={styles["sheet-titleblock"]}>
              <div className={styles["sheet-name"]}>
                Pearl <span className={styles["beta-tag"]}>Beta</span>
              </div>
              <div className={styles["sheet-sub"]}>DipYourCar</div>
            </div>
          </div>
          <button
            type="button"
            className={styles["sheet-close"]}
            onClick={close}
            aria-label="Close chat"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <path d="M6 6l12 12M6 18L18 6" />
            </svg>
          </button>
        </div>

        {/* Desktop floating close pill — hidden on mobile via CSS. */}
        <button
          type="button"
          className={styles["close-pill"]}
          onClick={close}
          aria-label="Close chat"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          >
            <path d="M6 6l12 12M6 18L18 6" />
          </svg>
        </button>

        <div className={styles.messages} ref={messagesRef}>
          {messages.length === 0 && active && (
            <>
              <div className={cn(styles.msg, styles.assistant)}>
                <div
                  className={cn(styles.avatar, styles["avatar-pearl"])}
                  aria-hidden="true"
                />
                <div className={styles.card}>
                  <div className={styles.meta}>
                    <span className={styles["meta-name"]}>Pearl</span>
                    <span className={styles["beta-tag"]}>Beta</span>
                    <span className={styles["meta-time"]}>JUST NOW</span>
                  </div>
                  <div className={styles.text}>
                    {proactiveText ?? WELCOME_TEXT}
                  </div>
                </div>
              </div>
              <Chips
                chips={
                  curatedChips && curatedChips.length >= 2
                    ? curatedChips
                    : WELCOME_CHIPS
                }
                onPick={handleSend}
              />
            </>
          )}

          {messages.map((m) => (
            <MessageCard
              key={m.id}
              message={m}
              onSubmitText={handleSend}
              onSubmitEmail={(email) => handleSend(`my email is ${email}`)}
            />
          ))}

          {/* Composing indicator. Single line, no cycling verbs —
            * "thinking → mixing → checking → polishing" reads as
            * performative personality, the kind of thing that makes
            * AI feel like AI. A quiet "Pearl" + three dots is enough
            * signal that work is happening. */}
          <div className={styles.composing} aria-live="polite">
            <span>Pearl</span>
            <span className={styles["composing-dots"]} aria-hidden="true">
              <i />
              <i />
              <i />
            </span>
          </div>
        </div>

        <Composer onSubmit={handleSend} disabled={pending} />

        <div className={styles["orb-row"]}>
          <VoiceOrb
            recorder={recorder}
            onTap={handleOrbTap}
            onCapture={handleVoiceBlob}
          />
        </div>
      </div>
    </div>
  )
}
